// AudioContext bootstrap. MUST be called from inside a user-gesture handler
// (autoplay policy + iOS unlock). Creates the context, plays a silent buffer to
// unlock iOS, resumes, then registers the worklet module.
import { ResonatorBankNode } from '../engine/resonator-node';
import { MasterChain } from './masterChain';
import { OceanBed } from './oceanBed';
import type { QualityTier } from '@shared/voice';

export interface BootedAudio {
  ctx: AudioContext;
  bank: ResonatorBankNode;
  master: MasterChain;
  ocean: OceanBed;
}

/** Run an async op with a timeout; retry `retries` times before rejecting. */
async function withTimeoutRetry<T>(op: () => Promise<T>, ms: number, retries: number): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await Promise.race([
        op(),
        new Promise<T>((_, rej) => setTimeout(() => rej(new Error('timeout')), ms)),
      ]);
    } catch (e) {
      lastErr = e;
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error('operation failed');
}

function unlockIOS(ctx: AudioContext): void {
  const buffer = ctx.createBuffer(1, 1, ctx.sampleRate);
  const src = ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(ctx.destination);
  src.start(0);
}

export async function bootAudio(tier: QualityTier, seed = 0x1234567): Promise<BootedAudio> {
  const ctx = new AudioContext({ latencyHint: 'interactive' });
  unlockIOS(ctx);
  // Poll resume up to ~2s WITHOUT awaiting resume() directly: a resume() called outside a
  // trusted gesture can stay pending forever, so we fire-and-forget and watch ctx.state.
  const deadline = performance.now() + 2000;
  while (ctx.state !== 'running' && performance.now() < deadline) {
    void ctx.resume().catch(() => {});
    await new Promise((r) => setTimeout(r, 50));
  }
  // Register the worklet defensively: on some iOS Safari versions addModule() can hang or reject
  // silently. Race it against a timeout and retry once, so boot fails loudly (the Enter overlay
  // shows an error) instead of leaving the user on a frozen "Surfacing…" with no sound.
  await withTimeoutRetry(() => ResonatorBankNode.register(ctx), 6000, 1);
  const bank = new ResonatorBankNode(ctx, { tier, seed });
  const master = new MasterChain(ctx);
  bank.connect(master.input);
  const ocean = new OceanBed(ctx);
  ocean.output.connect(master.ambientInput);
  return { ctx, bank, master, ocean };
}
